// InviteNewUserFormProps is a discriminated union type
// hence prop destructuring is not available unless a conditional check is being made
// to make sure a specific property exists before using it
/* eslint-disable react/destructuring-assignment */

import { Flex, Hint, Label, Link, Toggle } from '@lokalise/louis';
import { Form as FormikForm, setNestedObjectValues, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';

import type { Project } from '@app/api/team';
import MultiEmail from '@app/components/MultiEmail';
import SelectField from '@app/components/form/SelectField';

import { SplitView, SwitchWrapper } from '../styles';
import type { InviteNewUserFormProps, InviteNewUserSubmitValues, NumberSelectOption } from '../types';
import { LanguagesFields, Role } from '../types';
import { getInitialLanguagesOptions, getInviteNewUserFormLanguageOptions, handleFormLanguageChanges } from '../utils';

const projectOptions = (projects: Project[]) => projects.map(({ id, name }) => ({ value: id, label: name }));

const InviteNewUserForm = (props: InviteNewUserFormProps) => {
    const { isSubmitting, onReadyToValidate, onFormChange } = props;
    const [languageOptions, setLanguageOptions] = useState<{ value: number; label: string }[]>(
        getInitialLanguagesOptions(props.projects ?? props.activeProject),
    );
    const { values, setValues, setFieldValue, errors, validateForm, setTouched, setFieldError } =
        useFormikContext<InviteNewUserSubmitValues>();

    useEffect(() => {
        if (props.exceedingLimit) {
            setFieldError('contributors', 'Your team has reached the seat limit. Consider upgrading.');
        }
    }, [props.exceedingLimit, setFieldError]);

    useEffect(() => {
        if (values.role.value === Role.admin) {
            setFieldValue(LanguagesFields.contributableLanguages, null);
            setFieldValue(LanguagesFields.referenceLanguages, null);
        }
    }, [setFieldValue, values.role]);

    useEffect(() => {
        if (isSubmitting) {
            const validate = async () => {
                const formErrors = await validateForm();
                setTouched(setNestedObjectValues(formErrors, true));
                const isReady = Object.keys(formErrors).length === 0;
                onReadyToValidate({ isReady, form: values });
            };

            validate();
        }

        // We control form submission from outside FormikContext as the form will be used in different places
        // Hence this side effect shall only run when isSubmitting changes and do not depend on any other value
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitting]);

    const handleProjectChange = (project: { label: string; value: string }) => {
        if (project.value === values.project?.value) {
            return;
        }

        setValues({ ...values, project, referenceLanguages: null, contributableLanguages: null });
        const selectedProject = props.projects?.find((item) => item.id === project.value);
        if (selectedProject) {
            setLanguageOptions(getInviteNewUserFormLanguageOptions(selectedProject.languages));
        }
    };

    useEffect(() => {
        onFormChange(values);
    }, [onFormChange, values]);

    const onLanguageChange = (field: LanguagesFields, languages: readonly NumberSelectOption[]) => {
        const { referenceLanguages, contributableLanguages } = handleFormLanguageChanges({ field, languages, values });
        setValues({ ...values, contributableLanguages, referenceLanguages });
    };

    return (
        <FormikForm data-testid="inviteNewUserForm">
            <Flex gap={6}>
                <Label text="E-mail addresses">
                    <MultiEmail
                        value={values.contributors}
                        onChange={(value) => setFieldValue('contributors', value)}
                        error={errors.contributors}
                    />
                </Label>
                {props.projects && (
                    <Label
                        id="Project"
                        text={
                            <>
                                Project <Hint ariaLabel="Project hint">You can only add a new user to 1 project.</Hint>
                            </>
                        }
                    >
                        <SelectField
                            aria-labelledby="Project"
                            name="project"
                            options={projectOptions(props.projects)}
                            onChange={(project) => project && handleProjectChange(project)}
                        />
                    </Label>
                )}
                <SplitView>
                    <Label
                        id="Role"
                        text={
                            <>
                                Role{' '}
                                <Hint ariaLabel="Role hint">
                                    You can adjust administrative rights after you&apos;ve invited the user.{' '}
                                    <Link
                                        href="https://docs.lokalise.com/translation-workflow/contributors?_ga=2.10561971.624071806.1644913872-624637374.1609319460"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Learn more.
                                    </Link>
                                </Hint>
                            </>
                        }
                        underline
                    >
                        <SelectField
                            aria-labelledby="Role"
                            name="role"
                            isSearchable={false}
                            options={[
                                { value: Role.translator, label: 'Translator' },
                                { value: Role.admin, label: 'Administrator' },
                            ]}
                        />
                    </Label>
                    <Label
                        text={
                            <>
                                Reviewer{' '}
                                <Hint ariaLabel="Reviewer hint">Can mark translations as reviewed in the editor.</Hint>
                            </>
                        }
                        underline
                    >
                        <SwitchWrapper>
                            <Toggle
                                checked={values.isReviewer}
                                onChange={() => setFieldValue('isReviewer', !values.isReviewer)}
                            />
                        </SwitchWrapper>
                    </Label>
                </SplitView>
                <Flex gap={4} grow={1}>
                    <Label text="Languages" underline />
                    <Label
                        for="ViewableLanguage"
                        id="ViewableLanguage"
                        text={
                            <>
                                Reference languages (view){' '}
                                <Hint ariaLabel="Reference languages hint">Read-only access to these languages.</Hint>
                            </>
                        }
                    >
                        <SelectField
                            aria-labelledby="ViewableLanguage"
                            placeholder="Viewable languages"
                            name="referenceLanguages"
                            isDisabled={(props.projects && !values.project) || values.role.value === Role.admin}
                            options={languageOptions}
                            onChange={(value) => onLanguageChange(LanguagesFields.referenceLanguages, value)}
                            isMulti
                        />
                    </Label>
                    <Label
                        for="ContributableLanguage"
                        id="ContributableLanguage"
                        text={
                            <>
                                Contributable languages (edit){' '}
                                <Hint ariaLabel="Contributable languages hint">
                                    Read-write access to these languages.
                                </Hint>
                            </>
                        }
                    >
                        <SelectField
                            aria-labelledby="ContributableLanguage"
                            placeholder="Editable languages"
                            name="contributableLanguages"
                            isDisabled={(props.projects && !values.project) || values.role.value === Role.admin}
                            options={languageOptions}
                            onChange={(value) => onLanguageChange(LanguagesFields.contributableLanguages, value)}
                            isMulti
                        />
                    </Label>
                </Flex>
            </Flex>
        </FormikForm>
    );
};

export default InviteNewUserForm;
